import React from 'react';
import { Box, Typography, Link, Grid, IconButton, Container } from '@mui/material';
import { LinkedIn as LinkedInIcon, GitHub as GitHubIcon } from '@mui/icons-material';
import { useLanguage } from '../i18n';

const LandingFooter: React.FC = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const linkStyle = {
    color: 'rgba(255,255,255,0.5)',
    textDecoration: 'none',
    fontSize: '0.875rem',
    display: 'block',
    mb: 1.5,
    transition: 'color 0.2s',
    '&:hover': { color: '#A78BFA' },
  };

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        zIndex: 1,
        borderTop: '1px solid rgba(255,255,255,0.06)',
        background: 'rgba(5, 8, 22, 0.95)',
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ py: { xs: 6, md: 8 } }}>
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                <Box
                  component="img"
                  src="/image.png"
                  alt="KrElixir"
                  sx={{ height: 32, filter: 'brightness(0) invert(1)' }}
                />
              </Box>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.7, maxWidth: 300, mb: 3 }}>
                {t.footer.companyDesc}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
                {t.footer.followUs}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                {['LinkedIn', 'GitHub'].map((s) => (
                  <IconButton
                    key={s}
                    size="small"
                    component="a"
                    href={s === 'LinkedIn' ? 'https://linkedin.com/company/krelixir' : 'https://github.com/krelixir'}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: 2,
                      width: 36,
                      height: 36,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      '&:hover': { color: '#A78BFA', borderColor: 'rgba(139,92,246,0.3)' },
                    }}
                  >
                    {s === 'LinkedIn' ? <LinkedInIcon sx={{ fontSize: 18 }} /> : <GitHubIcon sx={{ fontSize: 18 }} />}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            {/* Product Links */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.1em', mb: 2, display: 'block' }}>
                {t.footer.product}
              </Typography>
              <Link href="#features" sx={linkStyle}>{t.footer.productLinks.features}</Link>
              <Link href="#how-it-works" sx={linkStyle}>{t.footer.productLinks.howItWorks}</Link>
              <Link href="#" sx={linkStyle}>{t.footer.productLinks.pricing}</Link>
              <Link href="#" sx={linkStyle}>{t.footer.productLinks.demo}</Link>
            </Grid>

            {/* Resources */}
            <Grid item xs={6} sm={3} md={2}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.1em', mb: 2, display: 'block' }}>
                {t.footer.resources}
              </Typography>
              <Link href="#" sx={linkStyle}>{t.footer.resourceLinks.docs}</Link>
              <Link href="#" sx={linkStyle}>{t.footer.resourceLinks.api}</Link>
              <Link href="#" sx={linkStyle}>{t.footer.resourceLinks.support}</Link>
              <Link href="#" sx={linkStyle}>{t.footer.resourceLinks.blog}</Link>
            </Grid>

            {/* Contact */}
            <Grid item xs={12} sm={6} md={4}>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', fontWeight: 700, letterSpacing: '0.1em', mb: 2, display: 'block' }}>
                {t.footer.contact}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1.5 }}>
                {t.footer.contactEmail}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 1.5 }}>
                {t.footer.contactPhone}
              </Typography>
              <Link
                href="https://krelixir.com/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ ...linkStyle, color: '#A78BFA', fontWeight: 600 }}
              >
                krelixir.com →
              </Link>
            </Grid>
          </Grid>
        </Box>

        {/* Bottom Bar */}
        <Box
          sx={{
            py: 3,
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.3)' }}>
            © {currentYear} KR Elixir Technology. {t.footer.allRightsReserved}.
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', textAlign: 'center' }}>
            {t.footer.tagline}
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default LandingFooter;
